import React, { useState, useEffect } from 'react';
import DocumentationList from './DocumentationList';
import DocumentationForm from './DocumentationForm';
import DocumentationPreviewModal from './DocumentationPreviewModal';
import Layout from "../../components/Layout/Layout";
import { useUser } from '../../contexts/UserContext';
import { FiPlus, FiAlertCircle, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';

function DocumentationPage() {
  const { user } = useUser();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Access Roles
  const userRoleId = Number(user?.role_id) || 0;
  const userDesignationId = Number(user?.designation_id) || 0;
  const currentUserId = Number(user?.id || user?.user_id) || 0;
  const verticalHeads = [26, 42, 43];
  const openAccessUsers = [276];

  const canCreate = userRoleId === 1 || verticalHeads.includes(userRoleId) || verticalHeads.includes(userDesignationId) || openAccessUsers.includes(currentUserId);
  const isAdmin = userRoleId === 1;

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEdit = (doc) => {
    setEditData(doc);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditData(null);
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-lg text-gray-600">
        Loading user information...
      </div>
    );
  }

  return (
    <Layout noSidebar={true}>
      <div className="p-4 bg-neutral-light min-h-full relative overflow-x-hidden">
        <div className="bg-white rounded shadow-sm px-4 py-4">
          
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b pb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800 tracking-tight">Project Documentation</h1>
              <p className="text-sm text-gray-500 mt-1">Centralized knowledge repository for all modules and documents.</p>
            </div>
            
            {canCreate && (
              <button 
                onClick={() => {
                  setEditData(null);
                  setShowForm(true);
                }}
                className="flex items-center gap-2 bg-main text-white px-5 py-2 rounded-lg shadow-md hover:bg-opacity-90 active:scale-95 transition-all font-semibold"
              >
                <FiPlus className="text-xl" />
                <span>Create Documentation</span>
              </button>
            )}
          </div>

          {/* Stats / Notice Bar */}
          <div className={`${isAdmin ? 'bg-red-50 border-red-400 text-red-700' : 'bg-red-50 border-red-400 text-red-700'} border-l-4 p-3 mb-6 rounded-r-lg`}>
            <div className="flex items-center">
              <FiAlertCircle className={`${isAdmin ? 'text-red-500' : 'text-red-500'} mr-3 text-lg`} />
              <p className="text-sm font-medium">
                {isAdmin 
                  ? "Administrator Mode: You can view, create, edit, and delete all documentation entries."
                  : canCreate 
                    ? "Vertical Head Mode: You can view, create, and edit documentation. Deletion is restricted to administrators." 
                    : "Viewer Mode: You can view and download all accessible project documentation."
                }
              </p>
            </div>
          </div>

          {/* List Section */}
          <div className="border rounded-lg overflow-hidden">
            <DocumentationList 
              refreshTrigger={refreshTrigger} 
              onPreview={setPreviewDoc} 
              onEdit={handleEdit}
              isAdmin={isAdmin}
              canEdit={canCreate}
            />
          </div>
        </div>

        {/* Create/Edit Overlay */}
        {showForm && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden relative border animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                <h3 className="text-lg font-bold text-gray-800">
                  {editData ? `Edit Documentation` : 'Create Documentation'}
                </h3>
                <button 
                  onClick={handleCloseForm}
                  className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition"
                >
                  <FiX className="text-lg" />
                </button>
              </div>
              
              <div className="p-6 max-h-[85vh] overflow-y-auto custom-scrollbar">
                 <DocumentationForm 
                   onRefresh={handleRefresh} 
                   editData={editData}
                   onCancel={handleCloseForm}
                 />
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewDoc && (
          <DocumentationPreviewModal 
            files={previewDoc?.attachments || []} 
            videoUrl={previewDoc?.video_url}
            isOpen={!!previewDoc} 
            onClose={() => setPreviewDoc(null)} 
          />
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </Layout>
  );
}

export default DocumentationPage;
